/**
 * The one floating tooltip bubble for every `.tip`/`.info-tip` element in the
 * app (see App.css section 11 for the class contract).
 *
 * Mounted once in AppShell. Listens for hover/focus on `.tip` elements at the
 * document level (event delegation - no per-trigger wiring needed anywhere
 * else) and portals a single bubble to <body>, positioned in viewport
 * coordinates from the trigger's getBoundingClientRect(). Because it is a
 * `position: fixed` node living outside every scroll pane, card, and dialog
 * in the tree, it can never be clipped by an ancestor's `overflow: hidden`
 * or painted behind a sibling - the bug a DOM-relative CSS ::after tooltip
 * had no way around.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 8;
const EDGE_PADDING = 8;

export default function TooltipLayer() {
  const [tooltip, setTooltip] = useState(null); // { text, below, left, right }
  const [pos, setPos] = useState(null); // { top, left } once measured
  const bubbleRef = useRef(null);
  const targetRef = useRef(null);

  const hide = useCallback(() => {
    targetRef.current = null;
    setTooltip(null);
    setPos(null);
  }, []);

  const show = useCallback((target) => {
    const text = target.getAttribute('data-tip');
    if (!text) return;
    targetRef.current = target;
    setTooltip({
      text,
      below: target.classList.contains('tip-below'),
      left: target.classList.contains('tip-left'),
      right: target.classList.contains('tip-right'),
    });
  }, []);

  useEffect(() => {
    const onOver = (event) => {
      const target = event.target.closest?.('.tip');
      if (target) show(target);
    };
    const onOut = (event) => {
      const target = event.target.closest?.('.tip');
      if (target && !(event.relatedTarget instanceof Node && target.contains(event.relatedTarget))) hide();
    };
    const onFocusIn = (event) => {
      const target = event.target.closest?.('.tip');
      if (target) show(target);
    };
    const onFocusOut = (event) => {
      const target = event.target.closest?.('.tip');
      if (target) hide();
    };
    // A trigger can move (the pane it's in scrolls) or vanish (the run this
    // button belonged to just finished polling and re-rendered) while its
    // tooltip is open - in both cases the bubble would otherwise be left
    // floating over stale coordinates or a control that no longer exists.
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('mousedown', hide);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('mousedown', hide);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [show, hide]);

  useLayoutEffect(() => {
    if (!tooltip || !targetRef.current || !bubbleRef.current) return;
    const targetRect = targetRef.current.getBoundingClientRect();
    const bubbleRect = bubbleRef.current.getBoundingClientRect();

    let top = tooltip.below ? targetRect.bottom + GAP : targetRect.top - GAP - bubbleRect.height;
    top = Math.min(Math.max(top, EDGE_PADDING), window.innerHeight - bubbleRect.height - EDGE_PADDING);

    let left;
    if (tooltip.left) {
      left = targetRect.right - bubbleRect.width;
    } else if (tooltip.right) {
      left = targetRect.left;
    } else {
      left = targetRect.left + targetRect.width / 2 - bubbleRect.width / 2;
    }
    left = Math.min(Math.max(left, EDGE_PADDING), window.innerWidth - bubbleRect.width - EDGE_PADDING);

    setPos({ top, left });
  }, [tooltip]);

  if (!tooltip) return null;

  return createPortal(
    <div
      ref={bubbleRef}
      className="tooltip-float"
      role="tooltip"
      style={pos ? { top: pos.top, left: pos.left, opacity: 1 } : { top: -9999, left: -9999, opacity: 0 }}
    >
      {tooltip.text}
    </div>,
    document.body,
  );
}
